import { useEffect, useRef, useState } from "react";
import {
  Link,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  ChevronRight,
  LogOut,
  Menu,
  Moon,
  Search,
  SlidersHorizontal,
  Sun,
  X,
} from "lucide-react";
import { useGetProfile } from "@/features/master-data/user/hooks/use-get-profile.hook";
import { removeAccessToken } from "@/features/auth/utils/token";
import { VersionSwitch } from "./VersionSwitch";
import { V2Theme, useV2Theme } from "./V2Theme";
import { v2Destinations } from "./navigation";
import { V2AsyncState } from "./V2Page";
import type { TPermissionKey } from "@agt/contract";
import type { ReactNode } from "react";

export function V2RootLayout() {
  return (
    <V2Theme>
      <Outlet />
    </V2Theme>
  );
}

export function V2Access({
  permission,
  masterOnly,
  children,
}: {
  permission?: TPermissionKey;
  masterOnly?: boolean;
  children: ReactNode;
}) {
  const profile = useGetProfile();
  const allowed = masterOnly
    ? profile.data?.role === "MASTER"
    : !permission || profile.data?.permissions?.includes(permission);
  return (
    <V2AsyncState
      loading={profile.isLoading}
      error={profile.error}
      onRetry={() => void profile.refetch()}
    >
      {allowed ? (
        children
      ) : (
        <section className="v2-error" role="alert">
          <h1>Access restricted</h1>
          <p>Your account does not have permission to open this page.</p>
          <Link className="v2-button" to="/v2/dashboard">
            Return to overview
          </Link>
        </section>
      )}
    </V2AsyncState>
  );
}

export function V2AppLayout() {
  const { data: profile } = useGetProfile();
  const { dark, compact, toggleTheme, toggleDensity } = useV2Theme();
  const [menuOpen, setMenuOpen] = useState(false);
  const [search, setSearch] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const searchInput = useRef<HTMLInputElement>(null);
  const visible = v2Destinations.filter((item) =>
    item.masterOnly
      ? profile?.role === "MASTER"
      : !item.permission || profile?.permissions?.includes(item.permission),
  );
  const destinations = visible.filter((item) =>
    `${item.label} ${item.group}`
      .toLowerCase()
      .includes(search.toLowerCase().trim()),
  );
  const current = v2Destinations.find(
    (item) =>
      location.pathname === `/v2/${item.path}` ||
      location.pathname.startsWith(`/v2/${item.path}/`),
  );
  useEffect(() => {
    document.title = `${current?.label ?? "Workspace"} | Travallis`;
    setMenuOpen(false);
  }, [location.pathname, current?.label]);
  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setMenuOpen(true);
        searchInput.current?.focus();
      }
      if (event.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);
  return (
    <div className="v2-shell">
      <a href="#v2-main" className="v2-skip">
        Skip to content
      </a>
      <aside
        className={`v2-sidebar ${menuOpen ? "v2-sidebar-open" : ""}`}
        aria-label="Main navigation"
      >
        <Link to="/v2/dashboard" className="v2-brand">
          <strong>Travallis</strong>
          <span>Finance workspace</span>
        </Link>
        <label className="v2-nav-search">
          <Search size={15} aria-hidden="true" />
          <span className="v2-sr-only">Find a page</span>
          <input
            ref={searchInput}
            name="navigation-search"
            type="search"
            placeholder="Find a page…"
            autoComplete="off"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <kbd>⌘ K</kbd>
        </label>
        <nav>
          {["Workspace", "Financial", "Master data", "Settings"].map(
            (group) => {
              const items = destinations.filter((item) => item.group === group);
              return items.length ? (
                <div className="v2-nav-group" key={group}>
                  <p>{group}</p>
                  {items.map((item) => (
                    <NavLink
                      to={`/v2/${item.path}`}
                      key={item.path}
                      onClick={() => {
                        setMenuOpen(false);
                        setSearch("");
                      }}
                    >
                      <item.icon
                        size={16}
                        strokeWidth={1.6}
                        aria-hidden="true"
                      />
                      <span>{item.label}</span>
                    </NavLink>
                  ))}
                </div>
              ) : null;
            },
          )}
          {search && !destinations.length && (
            <p className="v2-nav-empty" role="status">
              No accessible pages match this search.
            </p>
          )}
        </nav>
        <div className="v2-sidebar-account">
          <div>
            <strong>{profile?.name || "Your workspace"}</strong>
            <span>{profile?.email || "Loading profile…"}</span>
          </div>
          <button
            type="button"
            aria-label="Log out"
            onClick={() => {
              removeAccessToken();
              queryClient.clear();
              navigate("/v2/login", { replace: true });
            }}
          >
            <LogOut size={16} aria-hidden="true" />
          </button>
        </div>
      </aside>
      {menuOpen && (
        <button
          type="button"
          className="v2-nav-backdrop"
          aria-label="Close navigation"
          onClick={() => setMenuOpen(false)}
        />
      )}
      <div className="v2-workspace">
        <header className="v2-topbar">
          <div className="v2-breadcrumb">
            <button
              className="v2-icon-button v2-menu-trigger"
              type="button"
              aria-label="Toggle navigation"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {menuOpen ? (
                <X size={17} aria-hidden="true" />
              ) : (
                <Menu size={17} aria-hidden="true" />
              )}
            </button>
            <span>{current?.group ?? "Workspace"}</span>
            <ChevronRight size={14} aria-hidden="true" />
            <strong>{current?.label ?? "Page"}</strong>
          </div>
          <div className="v2-topbar-actions">
            <button
              className="v2-density-button"
              type="button"
              aria-pressed={compact}
              onClick={toggleDensity}
            >
              <SlidersHorizontal size={15} aria-hidden="true" />
              <span>{compact ? "Compact" : "Comfortable"}</span>
            </button>
            <button
              className="v2-icon-button"
              type="button"
              aria-label={
                dark ? "Switch to light theme" : "Switch to dark theme"
              }
              onClick={toggleTheme}
            >
              {dark ? (
                <Sun size={16} aria-hidden="true" />
              ) : (
                <Moon size={16} aria-hidden="true" />
              )}
            </button>
            <VersionSwitch original />
          </div>
        </header>
        <main id="v2-main" className="v2-main">
          <Outlet />
        </main>
        <footer className="v2-app-footer">
          <span>Travallis / Financial operations</span>
          <span>Your existing data and permissions</span>
        </footer>
      </div>
    </div>
  );
}

export function V2AuthLayout() {
  const location = useLocation();
  const isReset = location.pathname.includes("reset-password");
  return (
    <div className="v2-auth">
      <header>
        <Link to="/v2/login" className="v2-brand">
          <strong>Travallis</strong>
          <span>Finance workspace</span>
        </Link>
        <VersionSwitch original />
      </header>
      <main>
        <div className="v2-auth-content">
          <Outlet />
        </div>
      </main>
      <footer>
        {isReset
          ? "Reset your password to return to your workspace."
          : "Sign in with your existing account."}
      </footer>
    </div>
  );
}

export function V2ExportLayout() {
  return (
    <div className="v2-export-preview">
      <header className="v2-print-toolbar">
        <Link className="v2-button" to="/v2/dashboard">
          Return to workspace
        </Link>
        <span>Travallis / Document preview</span>
        <VersionSwitch original />
      </header>
      <main className="v2-print-document">
        <Outlet />
      </main>
    </div>
  );
}
